import { useEffect, useMemo, useState } from 'react';
import { adminBlogService, BlogCoverImage } from '@/services/blog';

interface Message {
  type: 'success' | 'error' | 'info';
  text: string;
}

export default function BlogCoverLibraryView() {
  const [covers, setCovers] = useState<BlogCoverImage[]>([]);
  const [coverFiles, setCoverFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const showMessage = (type: Message['type'], text: string) => {
    setMessages((prev) => [...prev, { type, text }]);
    setTimeout(() => setMessages((prev) => prev.slice(1)), 4500);
  };

  const loadCovers = async () => {
    try {
      const res = await adminBlogService.listCovers();
      if (res.success) setCovers(res.covers || []);
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to load covers');
    }
  };

  useEffect(() => {
    loadCovers();
  }, []);

  const previewCovers = useMemo(() => {
    return coverFiles.map((f) => ({ name: f.name, size: f.size }));
  }, [coverFiles]);

  const onSelectFiles = (files: FileList | null) => {
    if (!files) return;
    setCoverFiles(Array.from(files));
  };

  const upload = async () => {
    if (coverFiles.length === 0) {
      showMessage('error', 'Please select at least one cover image');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const res = await adminBlogService.uploadCovers(coverFiles, (percent) => {
        setUploadProgress(percent);
      });
      if (res.success) {
        const failed = (res.uploaded || []).filter((u) => u.error);
        const okCount = (res.uploaded || []).filter((u) => !u.error && u.url).length;
        showMessage('success', `Uploaded ${okCount} cover image(s)`);
        if (failed.length > 0) {
          showMessage('error', `Failed ${failed.length} image(s)`);
        }
        setCoverFiles([]);
        await loadCovers();
      }
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to upload');
    } finally {
      setIsUploading(false);
    }
  };

  const deleteCover = async (coverId: string) => {
    if (!window.confirm('Delete this cover image?')) return;

    setDeletingId(coverId);
    try {
      const res = await adminBlogService.deleteCover(coverId);
      if (res?.success) {
        showMessage('success', 'Cover deleted');
        await loadCovers();
      } else {
        showMessage('error', 'Failed to delete cover');
      }
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-cover-library">
      <div className="admin-cover-header">
        <div>
          <h1>Blog Cover Library</h1>
          <p className="admin-text-muted">Upload and manage blog cover images</p>
        </div>
        <button onClick={loadCovers} className="admin-btn-outline">Refresh</button>
      </div>

      {messages.length > 0 && (
        <div className="admin-messages">
          {messages.map((m, idx) => (
            <div key={idx} className={`admin-message admin-message--${m.type}`}>{m.text}</div>
          ))}
        </div>
      )}

      <div className="admin-cover-upload-card">
        <div className="admin-cover-upload-header">
          <span className="admin-font-medium">Upload Covers</span>
          <span className="admin-text-muted admin-text-sm">multi-select</span>
        </div>

        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => onSelectFiles(e.target.files)}
          className="admin-file-input"
        />

        {previewCovers.length > 0 && (
          <div className="admin-text-sm admin-text-muted">Selected {previewCovers.length} file(s)</div>
        )}

        {isUploading && (
          <div className="admin-upload-progress">
            <div className="progress-text">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        <div className="admin-cover-upload-actions">
          <button onClick={upload} disabled={isUploading || coverFiles.length === 0}>
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
          <button onClick={() => setCoverFiles([])} disabled={isUploading || coverFiles.length === 0} className="admin-btn-outline">
            Clear
          </button>
        </div>
      </div>

      <div className="admin-cover-grid-card">
        <div className="admin-cover-grid-header">
          <span className="admin-font-medium">Covers ({covers.length})</span>
        </div>

        {covers.length === 0 ? (
          <div className="admin-text-muted">No covers uploaded.</div>
        ) : (
          <div className="admin-cover-grid">
            {covers.map((c) => (
              <div key={c.id} className="admin-cover-item">
                <div className="admin-cover-image">
                  <img src={c.url} alt={c.id} loading="lazy" />
                </div>
                <div className="admin-cover-info">
                  <div className="admin-cover-id">{c.id}</div>
                  <button
                    className="admin-btn-sm danger"
                    disabled={isUploading || deletingId === c.id}
                    onClick={() => deleteCover(c.id)}
                  >
                    {deletingId === c.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
